import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getToken } from "@/utils/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import { FormInput } from "@/components/Base/Form";
import Button from "@/components/Base/Button";
import Lucide from "@/components/Base/Lucide";
import axios from "axios";
import Alert from "@/components/Base/Alert";

type EditRegionProps = {
  mode?: "add" | "edit";
};

interface State {
  id: string;
  name: string;
  countryCode?: string;
}

interface FormData {
  name: string;
  companyId?: string;
  stateIds: string[];
}

interface FormErrors {
  name?: string;
  stateIds?: string;
}

const EMPTY_FORM: FormData = {
  name: "",
  stateIds: [],
};

function EditRegion({ mode = "edit" }: EditRegionProps) {
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [loading, setLoading] = useState(mode === "edit");
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [states, setStates] = useState<State[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [stateConflicts, setStateConflicts] = useState<Record<string, { regionName: string }>>({});
  const navigate = useNavigate();
  const { id } = useParams();
  const conflictsCheckedRef = useRef(false);

  // Load states (StockLedger may not have states yet; backend can return empty list)
  useEffect(() => {
    const loadStates = async () => {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      try {
        setLoadingStates(true);
        const response = await axios.get(`${apiUrl}/states?countryCode=MY`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const loadedStates = response.data.data || [];
        setStates(loadedStates);

        // Optional conflict check (backend can return empty conflicts)
        const urlParams = new URLSearchParams(window.location.search);
        const companyId = formData.companyId || urlParams.get("companyId");

        if (loadedStates.length > 0) {
          const stateIds = loadedStates.map((s: State) => s.id);
          const params = new URLSearchParams();
          stateIds.forEach((sid: string) => params.append("stateIds[]", sid));
          if (id && mode === "edit") params.append("regionId", id);
          if (companyId) params.append("companyId", companyId);

          const conflictResponse = await axios.get(
            `${apiUrl}/regions/check-state-availability?${params.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const conflicts = conflictResponse.data.data?.conflicts || [];
          const conflictsMap: Record<string, { regionName: string }> = {};
          conflicts.forEach((conflict: any) => {
            conflictsMap[conflict.stateId] = { regionName: conflict.regionName };
          });
          setStateConflicts(conflictsMap);
        } else {
          setStateConflicts({});
        }
      } catch (error) {
        console.error("Failed to load states:", error);
      } finally {
        setLoadingStates(false);
      }
    };

    loadStates();
  }, [formData.companyId, id]);

  // Re-check conflicts when states are loaded (guarded)
  useEffect(() => {
    conflictsCheckedRef.current = false;
    const urlParams = new URLSearchParams(window.location.search);
    const companyIdFromUrl = urlParams.get("companyId");
    const companyId = formData.companyId || companyIdFromUrl;

    if (states.length > 0 && !conflictsCheckedRef.current) {
      conflictsCheckedRef.current = true;

      const checkConflicts = async () => {
        const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
        const token = getToken();

        try {
          const stateIds = states.map((s: State) => s.id);
          const params = new URLSearchParams();
          stateIds.forEach((sid: string) => params.append("stateIds[]", sid));
          if (id && mode === "edit") params.append("regionId", id);
          if (companyId) params.append("companyId", companyId);

          const conflictResponse = await axios.get(
            `${apiUrl}/regions/check-state-availability?${params.toString()}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const conflicts = conflictResponse.data.data?.conflicts || [];
          const conflictsMap: Record<string, { regionName: string }> = {};
          conflicts.forEach((conflict: any) => {
            conflictsMap[conflict.stateId] = { regionName: conflict.regionName };
          });
          setStateConflicts(conflictsMap);
        } catch (error) {
          console.error("Failed to check state conflicts:", error);
          conflictsCheckedRef.current = false;
        }
      };

      const timeoutId = setTimeout(() => {
        checkConflicts();
      }, 200);

      return () => clearTimeout(timeoutId);
    }
  }, [states, formData.companyId, id, mode]);

  useEffect(() => {
    const init = async () => {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();

      try {
        setLoading(true);

        const urlParams = new URLSearchParams(window.location.search);
        const companyId = urlParams.get("companyId");

        if (mode === "edit" && id) {
          const groupRes = await axios.get(`${apiUrl}/regions/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const group = groupRes.data;

          setFormData({
            name: group.name || "",
            companyId: group.companyId || "",
            stateIds: group.states?.map((s: State) => s.id) || [],
          });
        } else if (companyId) {
          setFormData((prev) => ({ ...prev, companyId }));
        }
      } catch (error) {
        console.error("Failed to initialize:", error);
        setSubmitError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [mode, id]);

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = "Region name is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const token = getToken();
      const urlParams = new URLSearchParams(window.location.search);
      const companyId = formData.companyId || urlParams.get("companyId");

      const apiData = {
        name: formData.name,
        stateIds: formData.stateIds || [],
      };

      if (mode === "add") {
        const queryParams = new URLSearchParams();
        if (companyId) queryParams.append("companyId", companyId);

        await axios.post(`${apiUrl}/regions?${queryParams.toString()}`, apiData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmitSuccess("Region created successfully!");
      } else {
        await axios.put(`${apiUrl}/regions/${id}`, apiData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubmitSuccess("Region updated successfully!");
      }

      setTimeout(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const projectId = searchParams.get("projectId");
        const companyId = formData.companyId;

        let redirectUrl = "/regions";
        if (projectId) redirectUrl += `?projectId=${projectId}`;
        else if (companyId) redirectUrl += `?companyId=${companyId}`;

        navigate(redirectUrl);
      }, 1500);
    } catch (error: any) {
      console.error("Submit failed:", error);
      const message = error.response?.data?.message || error.message || "An error occurred";
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const searchParams = new URLSearchParams(window.location.search);
    const projectId = searchParams.get("projectId");
    const companyId = formData.companyId;

    let redirectUrl = "/regions";
    if (projectId) redirectUrl += `?projectId=${projectId}`;
    else if (companyId) redirectUrl += `?companyId=${companyId}`;

    navigate(redirectUrl);
  };

  const handleStateToggle = async (stateId: string) => {
    setFormData((prev) => {
      const currentIds = prev.stateIds || [];
      const newIds = currentIds.includes(stateId)
        ? currentIds.filter((id) => id !== stateId)
        : [...currentIds, stateId];
      return { ...prev, stateIds: newIds };
    });
    if (errors.stateIds) {
      setErrors((prev) => ({ ...prev, stateIds: undefined }));
    }
  };

  if (loading && mode === "edit") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Lucide icon="Loader2" className="w-8 h-8 animate-spin mx-auto mb-2" />
          <div>Loading region...</div>
        </div>
      </div>
    );
  }

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6">
          <div className="col-span-12">
            <div className="flex flex-col mt-4 md:mt-0 md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">
                {mode === "add" ? "Add Region" : "Edit Region"}
              </div>
            </div>

            <div className="mt-3.5 grid grid-cols-12 xl:grid-cols-10 gap-y-7 lg:gap-y-10 gap-x-6">
              <div className="relative flex flex-col col-span-12 lg:col-span-9 xl:col-span-8 gap-y-7">
                <form onSubmit={handleSubmit}>
                  <div className="flex flex-col p-5 box box--stacked">
                    <div className="p-5 border rounded-[0.6rem] border-slate-200/60 dark:border-darkmode-400">
                      <div className="flex items-center pb-5 text-[0.94rem] font-medium border-b border-slate-200/60 dark:border-darkmode-400">
                        <Lucide icon="ChevronDown" className="w-5 h-5 stroke-[1.3] mr-2" />
                        Region Information
                      </div>

                      {submitError && (
                        <Alert variant="outline-danger" className="mt-5">
                          <Lucide icon="AlertTriangle" className="w-6 h-6" />
                          {submitError}
                        </Alert>
                      )}

                      {submitSuccess && (
                        <Alert variant="outline-success" className="mt-5">
                          <Lucide icon="CheckCircle" className="w-6 h-6" />
                          {submitSuccess}
                        </Alert>
                      )}

                      <div className="mt-5 grid grid-cols-12 gap-4">
                        <div className="col-span-12">
                          <label className="inline-block mb-2">
                            Region Name <span className="text-danger">*</span>
                          </label>
                          <FormInput
                            type="text"
                            placeholder="Enter region name"
                            value={formData.name}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                            className={errors.name ? "border-danger" : ""}
                          />
                          {errors.name && <div className="mt-2 text-danger text-xs">{errors.name}</div>}
                        </div>

                        {mode === "edit" && (
                          <div className="col-span-12">
                            <label className="inline-block mb-2">States</label>
                            <div className="border border-slate-200 dark:border-darkmode-400 rounded-lg p-4 max-h-64 overflow-y-auto">
                              {loadingStates ? (
                                <div className="flex items-center justify-center py-4">
                                  <Lucide icon="Loader2" className="w-5 h-5 animate-spin mr-2" />
                                  <span>Loading states...</span>
                                </div>
                              ) : states.length === 0 ? (
                                <div className="text-slate-500 text-sm">
                                  No states available (we can add the States module later).
                                </div>
                              ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                  {states.map((state) => {
                                    const isSelected = formData.stateIds?.includes(state.id) || false;
                                    const hasConflict = !!stateConflicts[state.id];
                                    const conflictInfo = stateConflicts[state.id];

                                    return (
                                      <label
                                        key={state.id}
                                        className={`flex items-center p-2 rounded transition-colors ${
                                          hasConflict && !isSelected
                                            ? "bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 cursor-not-allowed"
                                            : hasConflict
                                            ? "bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-400 dark:border-yellow-600 cursor-pointer"
                                            : "cursor-pointer hover:bg-slate-100 dark:hover:bg-darkmode-400 border border-transparent"
                                        } ${hasConflict && !isSelected ? "opacity-75" : ""}`}
                                        onClick={(e) => {
                                          if (hasConflict && !isSelected) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                          }
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          disabled={hasConflict && !isSelected}
                                          onChange={() => {
                                            if (!hasConflict || isSelected) {
                                              handleStateToggle(state.id);
                                            }
                                          }}
                                          className={`w-4 h-4 text-primary border-slate-300 rounded focus:ring-primary focus:ring-2 ${
                                            hasConflict && !isSelected ? "opacity-50 cursor-not-allowed" : ""
                                          }`}
                                          title={
                                            hasConflict && !isSelected
                                              ? `This state is already assigned to "${conflictInfo?.regionName}"`
                                              : ""
                                          }
                                        />
                                        <div className="ml-2 flex-1">
                                          <span
                                            className={`text-sm ${
                                              hasConflict
                                                ? "text-yellow-800 dark:text-yellow-200 font-semibold"
                                                : "text-slate-700 dark:text-slate-300"
                                            }`}
                                          >
                                            {state.name}
                                          </span>
                                          {hasConflict && (
                                            <div className="text-xs text-yellow-700 dark:text-yellow-300 mt-1 font-medium">
                                              Already in "{conflictInfo?.regionName}"
                                            </div>
                                          )}
                                        </div>
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            {formData.stateIds && formData.stateIds.length > 0 && (
                              <div className="mt-2 text-xs text-slate-500">
                                {formData.stateIds.length} state(s) selected
                              </div>
                            )}
                            {errors.stateIds && <div className="mt-2 text-danger text-xs">{errors.stateIds}</div>}
                          </div>
                        )}
                      </div>

                      <div className="flex justify-end mt-8 gap-3">
                        <Button type="button" variant="outline-secondary" onClick={handleCancel} disabled={loading}>
                          Cancel
                        </Button>
                        <Button type="submit" variant="primary" disabled={loading}>
                          {loading && <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />}
                          {mode === "add" ? "Create Region" : "Update Region"}
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </RoleGate>
    </ProtectedRoute>
  );
}

export default EditRegion;

